import {
  createElement,
  forwardRef,
  type ComponentProps,
  type HTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import { cn } from '@skupreme/shared/ui';

/*
 * Kept as className-forwarding components: the SCM board cards still extend
 * several of these with `styled(X)`, which only works if className reaches
 * the element.
 */
function block<E extends HTMLElement = HTMLDivElement>(
  displayName: string,
  tag: 'div' | 'span' | 'a',
  classes: string,
) {
  const Block = forwardRef<E, HTMLAttributes<E>>(
    ({ className, ...rest }, ref) =>
      createElement(tag, { ...rest, ref, className: cn(classes, className) }),
  );
  Block.displayName = displayName;
  return Block;
}

export const ColumnHeaderContainer = block(
  'ColumnHeaderContainer',
  'div',
  'sk:flex sk:items-center sk:justify-between',
);

export const RowHeaderContainer = block(
  'RowHeaderContainer',
  'div',
  'sk:flex sk:flex-row sk:items-center sk:justify-between',
);

export const ColumnHeaderText = block(
  'ColumnHeaderText',
  'div',
  'sk:flex sk:cursor-pointer sk:items-center sk:text-[15px] sk:leading-[18px] sk:font-bold sk:text-fg sk:hover:underline sk:[&_img]:mr-0.5',
);

export const ViewAllText = block<HTMLAnchorElement>(
  'ViewAllText',
  'a',
  'sk:cursor-pointer sk:text-xs sk:leading-3.5 sk:text-link sk:underline sk:hover:text-link sk:hover:underline',
);

type ColumnSubHeaderTextProps = HTMLAttributes<HTMLDivElement> & {
  color?: string;
};

export const ColumnSubHeaderText = forwardRef<
  HTMLDivElement,
  ColumnSubHeaderTextProps
>(({ className, color, style, ...rest }, ref) =>
  createElement('div', {
    ...rest,
    ref,
    className: cn(
      'sk:border-0 sk:border-b sk:border-solid sk:border-border sk:pb-1 sk:text-xs sk:leading-3.5 sk:text-fg-tertiary',
      className,
    ),
    style: color ? { borderColor: color, ...style } : style,
  }),
);
ColumnSubHeaderText.displayName = 'ColumnSubHeaderText';

type BorderProps = HTMLAttributes<HTMLDivElement> & {
  /** A caller's own colour for the rule; the kit's subtle border otherwise. */
  borderColor?: string;
};

export const Border = forwardRef<HTMLDivElement, BorderProps>(
  ({ className, borderColor, style, ...rest }, ref) =>
    createElement('div', {
      ...rest,
      ref,
      className: cn('sk:h-px sk:bg-border-subtle', className),
      style: borderColor ? { backgroundColor: borderColor, ...style } : style,
    }),
);
Border.displayName = 'Border';

export const BoxStyledLink = forwardRef<
  HTMLAnchorElement,
  ComponentProps<typeof Link>
>(({ className, ...rest }, ref) =>
  createElement(Link, {
    ...rest,
    ref,
    className: cn(
      'sk:max-w-full sk:text-fg sk:hover:text-fg sk:[&_.no-wrap]:whitespace-nowrap',
      className,
    ),
  }),
);
BoxStyledLink.displayName = 'BoxStyledLink';

export const BoxContainer = block(
  'BoxContainer',
  'div',
  'sk:mx-auto sk:my-4 sk:cursor-pointer sk:rounded-[5px] sk:bg-surface sk:p-1.25 sk:shadow-card sk:inset-shadow-hairline sk:transition-shadow sk:hover:shadow-md',
);

export const BoxHeader = block(
  'BoxHeader',
  'div',
  'sk:overflow-hidden sk:text-sm sk:leading-4 sk:font-bold sk:text-ellipsis sk:whitespace-nowrap sk:text-fg sk:[&_.supplier-name]:truncate',
);

type BoxTextProps = HTMLAttributes<HTMLSpanElement> & { wrapText?: boolean };

const BOX_TEXT =
  'sk:overflow-hidden sk:text-[10px] sk:leading-3 sk:font-bold sk:text-ellipsis sk:text-fg';

export const BoxText = forwardRef<HTMLSpanElement, BoxTextProps>(
  ({ className, wrapText, ...rest }, ref) =>
    createElement('span', {
      ...rest,
      ref,
      className: cn(
        BOX_TEXT,
        wrapText ? 'sk:whitespace-normal' : 'sk:whitespace-nowrap',
        className,
      ),
    }),
);
BoxText.displayName = 'BoxText';

export const BoxValue = forwardRef<HTMLSpanElement, BoxTextProps>(
  ({ className, ...rest }, ref) =>
    createElement(BoxText, {
      ...rest,
      ref,
      className: cn('sk:text-success sk:underline', className),
    }),
);
BoxValue.displayName = 'BoxValue';

export const BoxTextContainer = block(
  'BoxTextContainer',
  'div',
  'sk:my-1.25 sk:flex sk:w-full sk:items-center sk:justify-between',
);

type BorderBottomProps = HTMLAttributes<HTMLDivElement> & {
  borderBottom?: boolean;
};

export const BorderBottom = forwardRef<HTMLDivElement, BorderBottomProps>(
  ({ className, borderBottom, ...rest }, ref) =>
    createElement('div', {
      ...rest,
      ref,
      className: cn(
        borderBottom &&
          'sk:border-0 sk:border-b-2 sk:border-solid sk:border-current',
        className,
      ),
    }),
);
BorderBottom.displayName = 'BorderBottom';
